import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PersonalInterests = () => {
  const [activeHobby, setActiveHobby] = useState(0);

  const interests = [
  {
    category: "Technology & Innovation",
    icon: "Cpu",
    color: "primary",
    items: [
    {
      name: "Open Source Contributing",
      description: "Active contributor to React, Node.js, and various developer tools. Maintaining 3 popular npm packages.",
      image: "https://images.unsplash.com/photo-1681164315014-06bf36b2597a",
      imageAlt: "Developer working on open source code with multiple monitors showing GitHub repositories and code contributions",
      stats: "15+ repositories, 2.5K+ stars"
    },
    {
      name: "AI & Machine Learning",
      description: "Exploring the latest in AI/ML, building personal projects with TensorFlow and experimenting with LLMs.",
      image: "https://images.unsplash.com/photo-1719650592946-55163c4994cb",
      imageAlt: "Artificial intelligence visualization with neural network diagrams and data flowing through connected nodes on dark background",
      stats: "5 ML projects completed"
    },
    {
      name: "Tech Blogging",
      description: "Writing technical articles about web development, sharing knowledge with the developer community.",
      image: "https://images.unsplash.com/photo-1650521986932-86bbeded3fc2",
      imageAlt: "Person typing on laptop with technical blog post visible on screen, coffee cup and notebook nearby on wooden desk",
      stats: "50+ articles, 100K+ views"
    }]

  },
  {
    category: "Creative Pursuits",
    icon: "Palette",
    color: "accent",
    items: [
    {
      name: "Digital Photography",
      description: "Capturing urban landscapes and street photography. Love experimenting with light and composition.",
      image: "https://images.unsplash.com/photo-1613284852308-dad721710734",
      imageAlt: "Professional photographer holding DSLR camera taking street photography in urban environment with city lights in background",
      stats: "2K+ photos, 5 exhibitions"
    },
    {
      name: "UI/UX Design",
      description: "Designing interfaces and user experiences as a hobby. Creating design systems and prototypes.",
      image: "https://images.unsplash.com/photo-1675317120753-ce28b951e9e8",
      imageAlt: "Designer working on user interface mockups with Figma open on computer screen, sketches and color swatches on desk",
      stats: "20+ design projects"
    },
    {
      name: "Music Production",
      description: "Creating electronic music and soundscapes. Combining technology with creativity through digital audio.",
      image: "https://images.unsplash.com/photo-1636226570637-3fbda7ca09dc",
      imageAlt: "Home music studio setup with digital audio workstation on computer, MIDI keyboard, and audio equipment in modern room",
      stats: "15 tracks produced"
    }]

  },
  {
    category: "Active Lifestyle",
    icon: "Activity",
    color: "success",
    items: [
    {
      name: "Rock Climbing",
      description: "Indoor and outdoor climbing enthusiast. Love the problem-solving aspect and physical challenge.",
      image: "https://images.unsplash.com/photo-1581702980996-847f86b3776a",
      imageAlt: "Rock climber scaling indoor climbing wall with colorful holds, wearing safety harness and climbing shoes in modern gym",
      stats: "3 years experience, V6 grade"
    },
    {
      name: "Cycling",
      description: "Weekend road cycling and mountain biking. Exploring new trails and staying fit while enjoying nature.",
      image: "https://images.unsplash.com/photo-1668106678864-661671ac91bb",
      imageAlt: "Cyclist in athletic gear riding mountain bike on forest trail with sunlight filtering through trees",
      stats: "2000+ miles annually"
    },
    {
      name: "Hiking",
      description: "Exploring national parks and local trails. Finding inspiration in nature and disconnecting from screens.",
      image: "https://images.unsplash.com/photo-1529289252419-03cfc364face",
      imageAlt: "Hiker with backpack standing on mountain summit overlooking scenic valley landscape with dramatic clouds and sunset",
      stats: "25+ trails completed"
    }]

  },
  {
    category: "Learning & Growth",
    icon: "BookOpen",
    color: "warning",
    items: [
    {
      name: "Reading",
      description: "Avid reader of tech books, biographies, and science fiction. Always learning from industry leaders.",
      image: "https://images.unsplash.com/photo-1599661983564-f9d4f5dad6fd",
      imageAlt: "Cozy reading nook with open book, warm lighting, coffee cup, and bookshelf filled with technical and fiction books",
      stats: "24 books per year"
    },
    {
      name: "Language Learning",
      description: "Currently learning Japanese and improving Spanish. Fascinated by different cultures and communication.",
      image: "https://images.unsplash.com/photo-1646384958853-2b6934533849",
      imageAlt: "Language learning setup with Japanese textbooks, flashcards, and language learning app open on tablet",
      stats: "2 languages in progress"
    },
    {
      name: "Cooking",
      description: "Experimenting with international cuisines and molecular gastronomy. Applying systematic thinking to cooking.",
      image: "https://images.unsplash.com/photo-1721824293172-b8f0a400f882",
      imageAlt: "Modern kitchen with chef preparing gourmet meal, fresh ingredients arranged on marble counter, professional cooking equipment",
      stats: "50+ recipes mastered"
    }]

  }];


  const personalValues = [
  {
    icon: "Heart",
    title: "Empathy & Understanding",
    description: "Believing in the power of human connection and understanding different perspectives."
  },
  {
    icon: "Lightbulb",
    title: "Continuous Innovation",
    description: "Always seeking new ways to solve problems and improve existing solutions."
  },
  {
    icon: "Users",
    title: "Community Building",
    description: "Contributing to the developer community and helping others grow in their careers."
  },
  {
    icon: "Zap",
    title: "Work-Life Balance",
    description: "Maintaining harmony between professional growth and personal well-being."
  }];


  return (
    <section className="py-20 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-warning/10 text-warning px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Smile" size={16} />
            <span>Beyond the Code</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Personal Interests &
            <span className="text-gradient-brand block">Life Philosophy</span>
          </h2>
          
          <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            While I'm passionate about technology, I believe in living a well-rounded life. 
            Here's what drives me outside of coding and how it shapes my approach to development.
          </p>
        </div>

        {/* Interest Categories */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-4 gap-6 mb-8">
            {interests?.map((category, index) => (
            <button
              key={index}
              onClick={() => setActiveHobby(index)}
              className={`p-6 rounded-2xl border transition-brand text-left hover-lift ${
              activeHobby === index ?
              `border-${category?.color} bg-${category?.color}/10 shadow-brand` :
              'border-border bg-card hover:border-border/80'}`
              }>

                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
              activeHobby === index ? `bg-${category?.color}/20` : 'bg-muted'}`
              }>
                  <Icon
                  name={category?.icon}
                  size={24}
                  className={activeHobby === index ? `text-${category?.color}` : 'text-muted-foreground'} />

                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
              activeHobby === index ? 'text-foreground' : 'text-muted-foreground'}`
              }>
                  {category?.category}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {category?.items?.length} interests
                </p>
              </button>
            ))}
          </div>

          {/* Active Category Details */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {interests?.[activeHobby]?.items?.map((item, index) => (
            <div key={index} className="bg-card border border-border rounded-2xl overflow-hidden hover-lift transition-brand">
                <div className="aspect-video overflow-hidden">
                  <Image
                  src={item?.image}
                  alt={item?.imageAlt}
                  className="w-full h-full object-cover hover:scale-105 transition-brand" />

                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-card-foreground">
                      {item?.name}
                    </h4>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full bg-${interests?.[activeHobby]?.color}/10 text-${interests?.[activeHobby]?.color}`}>
                      {item?.stats}
                    </span>
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed">
                    {item?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Personal Values */}
        <div className="mb-20">
          <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-8 text-center">
            Core Personal Values
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {personalValues?.map((value, index) => (
            <div key={index} className="bg-card border border-border rounded-2xl p-6 text-center hover-lift transition-brand">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={value?.icon} size={24} className="text-primary" />
                </div>
                
                <h4 className="text-lg font-semibold text-card-foreground mb-3">
                  {value?.title}
                </h4>
                
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {value?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Fun Facts */}
        <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-success/5 rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Fun Facts About Me
            </h3>
            <p className="text-muted-foreground">
              Some interesting tidbits that make me who I am
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <Icon name="Coffee" size={32} className="text-warning mx-auto mb-3" />
              <div className="text-2xl font-bold text-foreground mb-2">1,200+</div>
              <div className="text-sm text-muted-foreground">Cups of coffee consumed while coding</div>
            </div>
            
            <div className="text-center">
              <Icon name="Mountain" size={32} className="text-success mx-auto mb-3" />
              <div className="text-2xl font-bold text-foreground mb-2">15</div>
              <div className="text-sm text-muted-foreground">National parks visited</div>
            </div>
            
            <div className="text-center">
              <Icon name="Camera" size={32} className="text-accent mx-auto mb-3" />
              <div className="text-2xl font-bold text-foreground mb-2">10K+</div>
              <div className="text-sm text-muted-foreground">Photos taken this year</div>
            </div>
            
            <div className="text-center">
              <Icon name="Music" size={32} className="text-primary mx-auto mb-3" />
              <div className="text-2xl font-bold text-foreground mb-2">500+</div>
              <div className="text-sm text-muted-foreground">Hours of music produced</div>
            </div>
            
            <div className="text-center">
              <Icon name="Globe" size={32} className="text-warning mx-auto mb-3" />
              <div className="text-2xl font-bold text-foreground mb-2">12</div>
              <div className="text-sm text-muted-foreground">Countries visited</div>
            </div>
            
            <div className="text-center">
              <Icon name="BookOpen" size={32} className="text-success mx-auto mb-3" />
              <div className="text-2xl font-bold text-foreground mb-2">200+</div>
              <div className="text-sm text-muted-foreground">Books in personal library</div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-lg text-muted-foreground italic">
              "The best code is written by developers who have rich experiences outside of coding. 
              Life informs art, and art informs code."
            </p>
          </div>
        </div>
      </div>
    </section>);

};

export default PersonalInterests;
